import { Center, Spinner } from '@chakra-ui/react'
import { updatedDiff } from 'deep-object-diff'
import type { FormikHelpers } from 'formik'
import { useFormik } from 'formik'
import * as React from 'react'
import { useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Redirect, Route, Switch, useRouteMatch } from 'react-router'
import useFetch, { CachePolicies } from 'use-http'
import { checkPermission, PermissionsEnum } from '../../../utils'
import PermissionGuard from '../../components/PermissionGuard'
import { SettingsSideMenu } from '../../components/Settings'
import useToast from '../../hooks/useToast'
import type { SettingsResponse } from '../../interfaces'
import type {
  AutoTaggingObj,
  ExtensionData,
  Extensions,
  RemoveBgProps,
  SettingsErrorResponse,
} from '../../interfaces/SettingsResponse'
import { BaseLayout } from '../../layouts'
import * as Yup from 'yup'
import {
  BackupBucketSettingsLayout,
  MultiFactorAuthenticationLayout,
  SingleSignOnSettingsLayout,
} from '../../layouts/Settings'
import { UserContext } from '../../providers'
import {
  ImageSettingsPage,
  MediaLibrarySettingsPage,
  NamedTransformsSettingsPage,
  VideoSettingsPage,
  SecuritySettingsPage,
  UrlEndpointFunctionPage,
  UrlEndpointFunctionTitlePage,
  UrlEndpointFunctionVersionPage,
} from '.'
import { CustomMetadataContext } from '../../providers/CustomMetadataProvider'
import { isEqual, isNull, isUndefined } from 'lodash'
import debounce from 'lodash/debounce'
import {
  getCmFieldValidatorMap,
  validateCmField,
} from '../../components/MediaLibrary/NewButton/uploadUtils'

const SettingsRouter = (): React.ReactElement => {
  const { path } = useRouteMatch()
  const toast = useToast()
  const { t } = useTranslation()
  const { cmFields = [] } = useContext(CustomMetadataContext)
  const [isInitialRender, setInitialRender] = React.useState(true)
  const [settingsResponse, setSettingsResponse] = React.useState<SettingsResponse>()

  const updatedCmField = React.useMemo(() => {
    return cmFields?.map(data => ({
      ...data,
      name: `IK_TEMP_CUSTOM_METADATA_${data.name}`,
      originalName: data.name,
    }))
  }, [cmFields])

  const cmFieldValidatorMap = getCmFieldValidatorMap(updatedCmField)

  const detectCmFields = React.useCallback(
    values => {
      const cmFieldsName = updatedCmField.map(data => data.name)
      const customMetadata = Object.keys(values)
        .filter(fieldName => cmFieldsName?.includes(fieldName))
        .reduce((ob, data) => ({ ...ob, [data]: values[data] }), {})
      const tempCustomMetadata = {}
      Object.keys(customMetadata).forEach(key => {
        if (
          key.split('IK_TEMP_CUSTOM_METADATA_')?.length &&
          key.split('IK_TEMP_CUSTOM_METADATA_')[1]
        )
          tempCustomMetadata[key.split('IK_TEMP_CUSTOM_METADATA_')[1]] =
            customMetadata[key]
      })
      return tempCustomMetadata
    },
    [updatedCmField],
  )

  const validationSchema = Yup.object().shape({
    upload: Yup.object().shape({
      ['customCoordinatesOb']: Yup.object()
        .shape({
          ['x-coordinate']: Yup.number().typeError(
            'x-coordinate must be a number',
          ),
          ['y-coordinate']: Yup.number().typeError(
            'y-coordinate must be a number',
          ),
          width: Yup.number().typeError('Width must be a number'),
          height: Yup.number().typeError('Height must be a number'),
        })
        .optional(),
    }),
  })


  const getInitialValues = React.useCallback(
    values => {
      let fileObject

      if (values?.upload?.tags && Array.isArray(values?.upload?.tags)) {
        fileObject = {
          ...fileObject,
          tags: values?.upload?.tags?.map((data: string) => ({
            label: data,
            value: data,
          })),
        }
      }
      if (values?.upload?.extensions?.length) {
        const aiTagData: AutoTaggingObj[] = []
        let removeBgData: RemoveBgProps
        const extensionsData: ExtensionData = {}
        values?.upload?.extensions?.forEach((ext: Extensions) => {
          if (
            ext?.name === 'aws-auto-tagging' ||
            ext?.name === 'google-auto-tagging'
          )
            aiTagData.push(ext)

          if (ext?.name === 'remove-bg') {
            removeBgData = {
              background: ext?.options?.bg_color
                ? 'background_color'
                : ext?.options?.bg_image_url
                ? 'background_image'
                : 'no_background',
              bgColor: ext?.options?.bg_color
                ? ext?.options?.bg_color
                : '#0450d5',
              bgImageURL: ext?.options?.bg_image_url
                ? ext?.options?.bg_image_url
                : '',
              semiTransparentCheck: ext.options?.semitransparency ?? false,
              shadowCheck: ext?.options?.add_shadow ?? false,
            }
            fileObject = { ...fileObject, removeBgData }
            extensionsData.removeBg = true
          }

          if (ext?.name === "ai-auto-description"){
            extensionsData.autoDescription = true
          }
        })
        if (aiTagData?.length) {
          fileObject = { ...fileObject, aiTagData }
          extensionsData.aiTag = true
        }
        fileObject = { ...fileObject, extensionsData }
      }
      if (values?.upload?.tags && Array.isArray(values?.upload?.tags)) {
        fileObject = {
          ...fileObject,
          tags: values?.upload?.tags?.map((data: string) => ({
            label: data,
            value: data,
          })),
        }
      }
      if (
        values?.upload?.customCoordinates &&
        Object.keys(values?.upload?.customCoordinates)?.length &&
        !Object.keys(values?.upload?.customCoordinates)?.every(
          key => values?.upload?.customCoordinates[key] === 0,
        )
      ) {
        fileObject = {
          ...fileObject,
          customCoordinatesOb: values?.upload?.customCoordinates,
        }
      }
      if (values?.upload?.webhookUrl) {
        fileObject = {
          ...fileObject,
          webhookUrl: values?.upload?.webhookUrl,
        }
      }
      if (
        values?.upload?.customMetadata &&
        Array.isArray(values?.upload?.customMetadata)
      ) {
        const customMetadata = values?.upload?.customMetadata?.reduce(
          (ob, data) => ({ ...ob, ...data  }),
          {},
        )
        const tempCustomMetadata = {}
        Object.keys(customMetadata).forEach(key => {
          tempCustomMetadata[`IK_TEMP_CUSTOM_METADATA_${key}`] =
            customMetadata[key]
        })
        fileObject= { ...fileObject, ...tempCustomMetadata }
      }
      if (updatedCmField?.length) {
        updatedCmField.forEach(cmData => {
          if (
            !(
              isUndefined(cmData?.schema?.defaultValue) ||
              isNull(cmData?.schema?.defaultValue)
            ) &&
            ((fileObject &&
              (isUndefined(fileObject[cmData?.name]) ||
                isNull(fileObject[cmData?.name]))) ||
              !fileObject)
          ) {
            fileObject = {
              ...fileObject,
              [cmData?.name]: cmData?.schema?.defaultValue,
            }
          }
        })
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('uniqueName')
      ) {
        fileObject = {
          ...fileObject,
          uniqueName: values?.upload?.uniqueName === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('isPrivateFile')
      ) {
        fileObject = {
          ...fileObject,
          isPrivateFile:
            values?.upload?.isPrivateFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('publishFile')
      ) {
        fileObject = {
          ...fileObject,
          publishFile:
            values?.upload?.publishFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteFile')
      ) {
        fileObject = {
          ...fileObject,
          overwriteFile:
            values?.upload?.overwriteFile === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteTags')
      ) {
        fileObject = {
          ...fileObject,
          overwriteTags:
            values?.upload?.overwriteTags === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteDescription')
      ) {
        fileObject = {
          ...fileObject,
          overwriteDescription:
            values?.upload?.overwriteDescription === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteAITags')
      ) {
        fileObject = {
          ...fileObject,
          overwriteAITags:
            values?.upload?.overwriteAITags === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('overwriteCustomMetadata')
      ) {
        fileObject = {
          ...fileObject,
          overwriteCustomMetadata:
            values?.upload?.overwriteCustomMetadata === 'true' ? true : false,
        }
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('preTransformation')
      ) {
        fileObject = {...fileObject, preTransformation: values?.upload?.preTransformation,}
      }
      if (
        values?.upload &&
        Object.keys(values?.upload)?.includes('postTransformation')
      ) {
        fileObject = {...fileObject, postTransformation: values?.upload?.postTransformation,}
      }
      if (
        values?.upload &&
        values?.upload?.savedExtensions &&
        Array.isArray(values?.upload?.savedExtensions)
      ) {
        fileObject = {...fileObject, savedExtensions: values?.upload?.savedExtensions,}
      }
      return fileObject
    },
    [updatedCmField],
  )

  const onSubmit = React.useCallback(
    values => {
      let submitData = {}
      if (Object.keys(values)?.length) {
        if (values?.tags && Array.isArray(values.tags))
          submitData['tags'] = values?.tags?.map(data => data.label)
        if (
          detectCmFields(values) &&
          Object.keys(detectCmFields(values))?.length
        ) {
          const cmOb = detectCmFields(values)
          submitData['customMetadata'] = Object.keys(cmOb).map(key => ({
            [key]: cmOb[key],
          }))
        }
        if (
          (values &&
            values?.customCoordinatesOb?.['x-coordinate'] &&
            values?.customCoordinatesOb?.['y-coordinate'] &&
            values?.customCoordinatesOb?.height &&
            values?.customCoordinatesOb.width) ||
          !(
            values &&
            values?.customCoordinatesOb?.['x-coordinate'] &&
            values?.customCoordinatesOb?.['y-coordinate'] &&
            values?.customCoordinatesOb?.height &&
            values?.customCoordinatesOb.width
          )
        )
          submitData['customCoordinates'] = {
            ['x-coordinate']: Number(
              values.customCoordinatesOb?.['x-coordinate'],
            ),
            ['y-coordinate']: Number(
              values.customCoordinatesOb?.['y-coordinate'],
            ),
            width: Number(values.customCoordinatesOb?.width),
            height: Number(values.customCoordinatesOb?.height),
          }

        if (values && values?.extensionsData) {
          let tempExt: Extensions[] = []
          if (values?.extensionsData?.aiTag) {
            tempExt = [...values?.aiTagData]
          }
          if (values?.extensionsData?.removeBg) {
            const options = {
              add_shadow: values?.removeBgData?.shadowCheck,
              semitransparency: values?.removeBgData?.semiTransparentCheck,
            }
            if (
              values?.removeBgData?.background === 'background_color' &&
              values?.removeBgData?.bgColor
            )
              options['bg_color'] = values?.removeBgData?.bgColor
            if (
              values?.removeBgData?.background === 'background_image' &&
              values?.removeBgData?.bgImageURL
            )
              options['bg_image_url'] = values?.removeBgData?.bgImageURL
            tempExt.push({
              name: 'remove-bg',
              options,
            })
          }
          if (values?.extensionsData?.autoDescription) {
            tempExt.push({
              name: 'ai-auto-description',
            })
          }
          submitData['extensions'] = tempExt
        }

        // Handle saved extensions
        if (values && Array.isArray(values?.savedExtensions)) {
          submitData['savedExtensions'] = values.savedExtensions
        }

        if (values && Object.keys(values)?.includes('webhookUrl'))
          submitData['webhookUrl'] = values?.webhookUrl

        if (values && Object.keys(values)?.includes('uniqueName'))
          submitData['uniqueName'] = values?.uniqueName.toString()

        if (values && Object.keys(values)?.includes('overwriteFile'))
          submitData['overwriteFile'] = values?.overwriteFile.toString()

        if (values && Object.keys(values)?.includes('isPrivateFile'))
          submitData['isPrivateFile'] = values?.isPrivateFile.toString()
        
        if (values && Object.keys(values)?.includes('publishFile')) {
          submitData['publishFile'] = values?.publishFile.toString()
        }

        if (values && Object.keys(values)?.includes('overwriteAITags'))
          submitData['overwriteAITags'] = values?.overwriteAITags.toString()

        if (values && Object.keys(values)?.includes('overwriteTags'))
          submitData['overwriteTags'] = values?.overwriteTags.toString()

        if (values && Object.keys(values)?.includes('overwriteDescription'))
          submitData['overwriteDescription'] = values?.overwriteDescription.toString()

        if (
          values &&
          Object.keys(values)?.includes('overwriteCustomMetadata')
        ) {
          submitData['overwriteCustomMetadata'] =
            values?.overwriteCustomMetadata.toString()
        }
        if (values && Object.keys(values)?.includes('preTransformation')) {
          submitData['preTransformation'] = values?.preTransformation.toString()
        }
        if (values && Object.keys(values)?.includes('postTransformation')) {
          submitData['postTransformation'] = isNull(values.postTransformation)
            ? []
            : values?.postTransformation
        }

        return submitData
      }
    },
    [detectCmFields],
  )

  const handleSubmit = async (
    values: SettingsResponse,
    _formikHelpers: FormikHelpers<SettingsResponse>,
  ): Promise<void> => {
    const networkBasedOptimization = 'networkBasedOptimization'
    let updatedObj = updatedDiff(form.initialValues, values)
    if (values?.upload && Object.keys(values?.upload))
      updatedObj['upload'] = onSubmit(values?.upload)

    if (
      !isEqual(
        form.initialValues.videoSettings.optimization.format.allowedVideoCodecs,
        values.videoSettings.optimization.format.allowedVideoCodecs,
      )
    ) {
      updatedObj['videoSettings'] = {
        optimization: {
          format: {
            allowedVideoCodecs:
              values.videoSettings.optimization.format.allowedVideoCodecs,
          },
        },
      }
    }

    const response = (await request.post(
      `/api/clients/${user?.clientNumber}/settings/v2`,
      updatedObj,
    )) as SettingsResponse | SettingsErrorResponse[]
    form.setSubmitting(true)
    if (request.error) {
      if ((response as SettingsErrorResponse[]).length) {
        // prettier-ignore
        (response as SettingsErrorResponse[]).forEach(errorObj => {
          const indexOffirstSeparator = errorObj.field.indexOf('.')

          _formikHelpers.setFieldError(
            errorObj.field.slice(Number(indexOffirstSeparator) + 1),
            errorObj.message,
          )
        })
      } else {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
      }
      return
    }

    toast({
      description: t(
        'successfully_saved_settings',
        'Successfully saved settings',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })

    await getData()
    void refetchUser()
  }

  const form = useFormik<SettingsResponse>({
    initialValues: {
      optimization: {
        autoFormat: 'false',
        autoQuality: 'false',
        useDataSaverMode: 'false',
        defaultQuality: 80,
      },
      device: {
        enabled: false,
        desktop: { width: 0, height: 0 },
        mobile: { width: 0, height: 0 },
      },
      security: { signed: 'false', onlySignedAiRequests: 'false', named: 'false' },
      namedTransform: {},
      upload: { uniqueName: 'true', publishFile: 'true' },
      advanced: {
        autoRotate: 'false',
        uploadOriginalImages: 'false',
        useChromaSubsampling: 'false',
        metadata: 'none',
        pngCompressionMode: '',
        autoConvertSvgs: false,
        dprLastStepOnly: false,
      },
      autoAvifFormat: false,
      videoSettings: {
        optimization: {
          format: {
            enabled: false,
            allowedFormats: ['mp4', 'webm'],
            allowedVideoCodecs: ['h264', 'vp9'],
          },
          quality: { enabled: false, default: 80 },
          polling: { attempts: 3, wait: 5000 },
        },
        security: { onlyNamedTransformations: false, onlySigned: false },
        transformation: { cropAtMaxAsCropAtMaxEnlarge: false },
      },
    },
    onSubmit: handleSubmit,
    validateOnChange: false,
    validate: values => {
      const errors: { [field: string]: object } = {
        device: {
          desktop: {},
          mobile: {},
        },
      }
      if (values.device.enabled) {
        let isError = false
        for (const [device, dimensions] of Object.entries(values.device)) {
          for (const [dimension, value] of Object.entries(dimensions)) {
            if (typeof value !== 'number' || value < 0) {
              isError = true
              errors.device[device][
                dimension
              ] = `Invalid ${device} ${dimension}.`
            }
          }
        }
        if (isError) {
          return errors
        }
      }
      return validateCmField(
        values?.upload,
        updatedCmField,
        cmFieldValidatorMap,
        true,
      )
    },
    validationSchema: validationSchema,
  })

  const debouncedValidate = React.useMemo(
    () => debounce(form.validateForm, 500),
    [form.validateForm],
  )

  useEffect(() => {
    debouncedValidate(form.values)
  }, [form.values, debouncedValidate])

  const { user, refetch: refetchUser } = useContext(UserContext)

  const { request, loading } = useFetch<SettingsResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
    loading: false,
  })

  const getData = async (): Promise<void> => {
    if (
      user?.clientNumber &&
      checkPermission({
        user,
        permission: PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW,
      })
    ) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/settings`,
      )
      setSettingsResponse(response)
      if (isInitialRender) setInitialRender(false)
    }
  }

  useEffect(() => {
    if (settingsResponse && form)
      void form.resetForm({
        values: {
          ...settingsResponse,
          upload: getInitialValues({
            upload: {
              ...settingsResponse?.upload,
              customMetadata: user?.customMetadata,
            },
          }),
        },
      })
  }, [settingsResponse, cmFields, user])

  useEffect(() => {
    void getData()
  }, [])

  return (
    <BaseLayout menu={<SettingsSideMenu />}>
      <Switch>
        <Route path={`${path}`} exact>
          {checkPermission({
            user,
            permission: PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW,
          }) ? (
            <Redirect to={`${path}/images`} />
          ) : checkPermission({
              user,
              permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
            }) ? (
            <Redirect to={`${path}/media-library`} />
          ) : (
            <Redirect to=".." />
          )}
        </Route>

        <Route path={`${path}/images`} exact>
          {loading && !isInitialRender ? (
            <Center height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          ) : (
            <PermissionGuard
              permission={PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <ImageSettingsPage {...form} />
                ) : (
                  <Redirect to={path} />
                )
              }
            </PermissionGuard>
          )}
        </Route>

        <Route path={`${path}/videos`} exact>
          {loading && !isInitialRender ? (
            <Center height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          ) : (
            <PermissionGuard
              permission={PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <VideoSettingsPage {...form} />
                ) : (
                  <Redirect to={path} />
                )
              }
            </PermissionGuard>
          )}
        </Route>
        <Route path={`${path}/named-transforms`} exact>
          {loading && !isInitialRender ? (
            <Center height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          ) : (
            <PermissionGuard
              permission={PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <NamedTransformsSettingsPage form={form} getData={getData} />
                ) : (
                  <Redirect to={path} />
                )
              }
            </PermissionGuard>
          )}
        </Route>
        <Route path={`${path}/media-library`} exact>
          {loading && !isInitialRender ? (
            <Center height="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="appPaleGrey"
                color="appBlue"
                size="xl"
              />
            </Center>
          ) : (
            <PermissionGuard
              permission={PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW}
            >
              {hasPermission =>
                hasPermission ? (
                  <MediaLibrarySettingsPage form={form} />
                ) : (
                  <PermissionGuard
                    permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
                  >
                    {(hasMlMgPermission: boolean) =>
                      hasMlMgPermission ? (
                        <MediaLibrarySettingsPage />
                      ) : (
                        <Redirect to={path} />
                      )
                    }
                  </PermissionGuard>
                )
              }
            </PermissionGuard>
          )}
        </Route>
        <Route path={`${path}/backup-bucket`} exact>
          <PermissionGuard
            permission={PermissionsEnum.SELF_IMAGE_SETTINGS_VIEW}
          >
            {(hasPermission: boolean) =>
              hasPermission ? (
                <BackupBucketSettingsLayout />
              ) : (
                <Redirect to={path} />
              )
            }
          </PermissionGuard>
        </Route>
        <Route path={`${path}/url-endpoint-functions`} exact>
          <PermissionGuard permission={PermissionsEnum.SELF_INTEGRATION_VIEW}>
            {(hasPermission: boolean) =>
              hasPermission ? (
                <UrlEndpointFunctionPage />
              ) : (
                <Redirect to={path} />
              )
            }
          </PermissionGuard>
        </Route>

         <Route path={`${path}/url-endpoint-functions/:title`} exact>
          <PermissionGuard permission={PermissionsEnum.SELF_INTEGRATION_VIEW}>
            {(hasPermission: boolean) =>
              hasPermission ? (
                <UrlEndpointFunctionTitlePage />
              ) : (
                <Redirect to={path} />
              )
            }
          </PermissionGuard>
        </Route>
            <Route path={`${path}/url-endpoint-functions/:title/:version`} exact>
          <PermissionGuard permission={PermissionsEnum.SELF_INTEGRATION_VIEW}>
            {(hasPermission: boolean) =>
              hasPermission ? (
                <UrlEndpointFunctionVersionPage />
              ) : (
                <Redirect to={path} />
              )
            }
          </PermissionGuard>
        </Route>
        {user?.permissions.includes('SELF_TEAM_MANAGE') ? (
          <Route path={`${path}/multi-factor-auth`} exact>
            <MultiFactorAuthenticationLayout />
          </Route>
        ) : (
          <Redirect to={path} />
        )}
        {user?.permissions.includes('SELF_TEAM_MANAGE') ? (
          <Route path={`${path}/single-sign-on`} exact>
            <SingleSignOnSettingsLayout />
          </Route>
        ) : (
          <Redirect to={path} />
        )}
        <PermissionGuard
          permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}
        >
          {(hasPermission: boolean) =>
            hasPermission ? (
              <Route path={`${path}/advanced-security`} exact>
                <SecuritySettingsPage />
              </Route>
            ) : (
              <Redirect to={path} />
            )
          }
        </PermissionGuard>
        <PermissionGuard permission={PermissionsEnum.SELF_TEAM_MANAGE}>
          {(hasPermission: boolean) =>
            hasPermission ? (
              <>
                <Route path={`${path}/multi-factor-auth`} exact>
                  <MultiFactorAuthenticationLayout />
                </Route>
                <Route path={`${path}/single-sign-on`} exact>
                  <SingleSignOnSettingsLayout />
                </Route>
              </>
            ) : (
              <Redirect to={path} />
            )
          }
        </PermissionGuard>
      </Switch>
    </BaseLayout>
  )
}

export default SettingsRouter
